import I18n from 'I18n';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { Button } from 'foundations-components/transitional/components/Button';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { Select } from 'foundations-components/transitional/components/Select';
import { Toggle } from 'foundations-components/transitional/components/Toggle';
import { useCallback } from 'react';
import { getDeepValue } from 'ContentUtils/helpers/ObjectHelpers';
import { DropdownToolContent } from './DropdownToolContent';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
function isValidChoices(choices) {
  return Array.isArray(choices) && choices.every(c => Array.isArray(c) && c.length === 2 && typeof c[0] === 'string' && typeof c[1] === 'string');
}
function getFieldValue(field, path, fieldOverrides) {
  const overrideValue = getDeepValue(fieldOverrides, path);
  return overrideValue !== undefined ? overrideValue : field.default;
}
function matchesFieldPattern(field, type, ...keywords) {
  if (field.type !== type) return false;
  const lowerName = field.name.toLowerCase();
  return keywords.every(k => lowerName.includes(k));
}
export function findIconSiblings(fields, iconField, basePath, fieldOverrides) {
  const siblings = {};
  const siblingFields = fields.filter(f => f.name !== iconField.name);
  const showIconField = siblingFields.find(f => matchesFieldPattern(f, 'boolean', 'show', 'icon'));
  if (showIconField) {
    const showIconPath = [...basePath, showIconField.name];
    siblings.showIcon = {
      field: showIconField,
      path: showIconPath,
      value: getFieldValue(showIconField, showIconPath, fieldOverrides)
    };
  }
  const positionField = siblingFields.find(f => matchesFieldPattern(f, 'choice', 'icon', 'position'));
  if (positionField) {
    const positionPath = [...basePath, positionField.name];
    siblings.position = {
      field: positionField,
      path: positionPath,
      value: getFieldValue(positionField, positionPath, fieldOverrides)
    };
  }
  return siblings;
}
export function IconTool({
  info,
  config,
  onFieldChange
}) {
  var _showIconSibling$valu, _positionSibling$valu;
  const {
    path,
    currentValue,
    siblings
  } = info;
  const iconValue = currentValue;
  const showIconSibling = siblings === null || siblings === void 0 ? void 0 : siblings.showIcon;
  const positionSibling = siblings === null || siblings === void 0 ? void 0 : siblings.position;
  const showIconPath = showIconSibling === null || showIconSibling === void 0 ? void 0 : showIconSibling.path;
  const positionPath = positionSibling === null || positionSibling === void 0 ? void 0 : positionSibling.path;
  const showIconValue = showIconSibling ? Boolean((_showIconSibling$valu = showIconSibling.value) !== null && _showIconSibling$valu !== void 0 ? _showIconSibling$valu : showIconSibling.field.default) : true;
  const positionValue = positionSibling ? String((_positionSibling$valu = positionSibling.value) !== null && _positionSibling$valu !== void 0 ? _positionSibling$valu : positionSibling.field.default) : 'left';
  const handleShowIconChange = useCallback(e => {
    if (showIconPath) {
      onFieldChange(showIconPath, e.target.checked);
    }
  }, [showIconPath, onFieldChange]);
  const handleRemoveIcon = useCallback(() => {
    onFieldChange(path, null);
  }, [path, onFieldChange]);
  const handlePositionChange = useCallback(option => {
    if (!option || !positionPath) return;
    onFieldChange(positionPath, option.value);
  }, [positionPath, onFieldChange]);
  const iconName = (iconValue === null || iconValue === void 0 ? void 0 : iconValue.name) || I18n.text('ContentComponents.cmv2.fields.icon.noneSelected');
  return /*#__PURE__*/_jsxs(DropdownToolContent, {
    info: info,
    config: config,
    children: [showIconSibling && /*#__PURE__*/_jsxs(Flex, {
      alignItems: "center",
      justifyContent: "space-between",
      children: [/*#__PURE__*/_jsx(Text, {
        children: I18n.text('inpage.fieldSettingsToolbar.icon.showIcon')
      }), /*#__PURE__*/_jsx(Toggle, {
        checked: showIconValue,
        onChange: handleShowIconChange,
        size: "extra-small"
      })]
    }), showIconValue && /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsxs(Flex, {
        flexDirection: "column",
        gap: "200",
        children: [/*#__PURE__*/_jsxs(Flex, {
          alignItems: "center",
          justifyContent: "space-between",
          children: [/*#__PURE__*/_jsx(Text, {
            children: /*#__PURE__*/_jsx("strong", {
              children: I18n.text('inpage.fieldSettingsToolbar.icon.title')
            })
          }), /*#__PURE__*/_jsx(Button, {
            use: "link",
            size: "small",
            onClick: handleRemoveIcon,
            disabled: !(iconValue !== null && iconValue !== void 0 && iconValue.name),
            children: I18n.text('ContentComponents.cmv2.fields.icon.removeLabel')
          })]
        }), /*#__PURE__*/_jsx(Text, {
          color: "text-core-subtle",
          children: iconName
        })]
      }), positionSibling && isValidChoices(positionSibling.field.choices) && /*#__PURE__*/_jsx(FormControl, {
        label: I18n.text('inpage.fieldSettingsToolbar.icon.position'),
        children: /*#__PURE__*/_jsx(Select, {
          value: positionValue,
          onChange: handlePositionChange,
          options: positionSibling.field.choices.map(([value, label]) => ({
            value,
            text: label
          })),
          anchorType: "button"
        })
      })]
    })]
  });
}